// Adventures view - displays marketplace adventures

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Signal icon SVG
const signalIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <path d="M2 20h.01" />
  <path d="M7 20v-4" />
  <path d="M12 20v-8" />
  <path d="M17 20V8" />
  <path d="M22 4v16" />
</svg>`;

// Clock icon SVG
const clockIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <circle cx="12" cy="12" r="10" />
  <polyline points="12 6 12 12 16 14" />
</svg>`;

/**
 * Adventures view component
 * Displays available adventures from the marketplace
 */
class AdventuresView extends BaseComponent {
  static properties = {
    _adventures: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<{id: string, name: string, description: string, capsuleImage: string|null, slug: string, difficulty: string|null, estimatedTime: string}>} */
    this._adventures = [];
    this._loading = true;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadAdventures();
  }

  async _loadAdventures() {
    this._loading = true;

    try {
      const data =
        /** @type {Array<{id: string, name: string, description: string, capsule_image_url: string, slug: string, level: number, duration: string}>} */ (
          await api.getMarketplaceAdventures()
        );
      this._adventures = data.map((adventure) => ({
        id: adventure.id,
        name: adventure.name,
        description: adventure.description,
        capsuleImage: api.getImageUrl(adventure.capsule_image_url),
        slug: adventure.slug,
        difficulty: adventure.level ? `Level ${adventure.level}` : null,
        estimatedTime: adventure.duration,
      }));
    } catch (error) {
      console.error('Failed to load adventures:', error);
    } finally {
      this._loading = false;
    }
  }

  /** @param {{capsuleImage: string|null}} adventure */
  _getCoverStyle(adventure) {
    if (adventure.capsuleImage) {
      return `background-image: url('${adventure.capsuleImage}')`;
    }
    return 'background: linear-gradient(135deg, #2d3561 0%, #c05c7e 100%)';
  }

  /** @param {string} slug */
  _handleStartAdventure(slug) {
    router.navigate(`/adventures/${slug}/start`);
  }

  render() {
    return html`
      ${
        this._loading
          ? html`
            <div class="text-center text-muted py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading adventures...</p>
            </div>
          `
          : html`
            <div class="d-flex flex-wrap gap-4">
              ${this._adventures.map(
                (adventure) => html`
                  <div style="flex: 1 1 300px; min-width: 300px; max-width: 350px;">
                    <div class="card h-100">
                      <div
                        class="card-img-top position-relative"
                        style="height: 200px; background-size: cover; background-position: center; ${this._getCoverStyle(
                          adventure,
                        )}"
                      >
                        ${
                          !adventure.capsuleImage
                            ? html`
                              <div
                                class="position-absolute top-50 start-50 translate-middle text-white text-center w-100 px-3"
                              >
                                <h2 class="display-6 fw-bold">${adventure.name}</h2>
                              </div>
                            `
                            : ''
                        }
                      </div>
                      <div class="card-body d-flex flex-column">
                        <h5 class="card-title">${adventure.name}</h5>
                        <p class="card-text text-muted">${adventure.description}</p>
                        <div class="d-flex gap-3 text-muted small mb-3">
                          ${adventure.difficulty ? html` <span>${signalIcon}${adventure.difficulty}</span> ` : ''}
                          ${adventure.estimatedTime ? html` <span>${clockIcon}${adventure.estimatedTime}</span> ` : ''}
                        </div>
                        <button
                          class="btn btn-primary mt-auto"
                          @click="${() => this._handleStartAdventure(adventure.slug)}"
                        >
                          Start Adventure
                        </button>
                      </div>
                    </div>
                  </div>
                `,
              )}
            </div>
          `
      }
    `;
  }
}

customElements.define('adventures-view', AdventuresView);
